import { useEffect, useMemo, useState } from "react";
import { Plus, Minus, Trash2, ShoppingBag, ChevronDown, ChevronUp, Loader2, ArrowRight } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { TableMap, TabPanel } from "./Tables";

function TakeawayCart({ outlet, recipes, onCheckout, onBack }) {
  const channels = (outlet?.sales_channels || ["Takeaway", "Delivery"]).filter((c) => c !== "Dine-in");
  const [channel, setChannel] = useState(channels[0] || "Takeaway");
  const [cart, setCart] = useState({});
  const items = Object.values(cart).filter((c) => c.qty > 0);
  const defaultGst = outlet?.default_gst_rate || 5;
  const subtotal = items.reduce((a, b) => a + b.qty * b.unit_price, 0);
  const bump = (r, d) => setCart((c) => {
    const cur = c[r.id] || { recipe_id: r.id, name: r.name, unit_price: r.selling_price, qty: 0, gst_rate: r.gst_rate ?? defaultGst, hsn_sac: r.hsn_sac || outlet?.default_hsn_sac || "2106", status: "served" };
    return { ...c, [r.id]: { ...cur, qty: Math.max(0, cur.qty + d) } };
  });
  const grouped = useMemo(() => { const g = {}; for (const r of recipes) if (r.available !== false) (g[r.category] = g[r.category] || []).push(r); return g; }, [recipes]);
  return (
    <section className="pos-layout" data-testid="takeaway-view">
      <div className="pos-menu panel">
        <div className="panel-head"><div><span className="overline">QUICK ORDER · NO TABLE</span><h2>Takeaway / Delivery</h2></div><button className="secondary-button" onClick={onBack} data-testid="takeaway-back">← Tables</button></div>
        <div className="pos-channel-row">
          {channels.map((c) => <button key={c} className={channel === c ? "chip active" : "chip"} onClick={() => setChannel(c)} data-testid={`pos-channel-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}>{c}</button>)}
        </div>
        {Object.entries(grouped).map(([cat, list]) => (
          <div key={cat} className="pos-cat"><span className="overline">{cat}</span>
            <div className="pos-grid">
              {list.map((r) => (
                <div key={r.id} className="pos-tile" data-testid={`pos-item-${r.id}`}>
                  <b>{r.name}</b><span className="muted-label">GST {r.gst_rate ?? defaultGst}%</span><strong>{money(r.selling_price)}</strong>
                  <div className="qty-row">
                    <button onClick={() => bump(r, -1)} disabled={!cart[r.id]?.qty} data-testid={`pos-dec-${r.id}`}><Minus size={14} /></button>
                    <span>{cart[r.id]?.qty || 0}</span>
                    <button onClick={() => bump(r, 1)} data-testid={`pos-inc-${r.id}`}><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <aside className="pos-cart panel">
        <div className="panel-head"><div><span className="overline">CURRENT TICKET</span><h2><ShoppingBag size={18} style={{ verticalAlign: "-3px", marginRight: 6 }} />{channel}</h2></div><button className="text-button" onClick={() => setCart({})} data-testid="pos-clear-cart"><Trash2 size={13} /> Clear</button></div>
        <div className="pos-lines">
          {!items.length && <div className="empty small">No items in ticket</div>}
          {items.map((it) => <div className="pos-line" key={it.recipe_id} data-testid={`pos-line-${it.recipe_id}`}><div><b>{it.name}</b><span>{it.qty} × {money(it.unit_price)}</span></div><strong>{money(it.qty * it.unit_price)}</strong></div>)}
        </div>
        <div className="pos-totals"><div className="pos-grand"><span>Subtotal (before GST)</span><strong>{money(subtotal)}</strong></div></div>
        <button className="primary-button full kot-big" disabled={!items.length} onClick={() => onCheckout({ draft: { channel, items } })} data-testid="takeaway-checkout"><ArrowRight size={16} /> Proceed to Final Bill</button>
      </aside>
    </section>
  );
}

export default function POSView({ branch, outlet, recipes, onCheckout, initialTable, onInitialUsed }) {
  const [tables, setTables] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [tab, setTab] = useState(null);
  const [takeaway, setTakeaway] = useState(false);
  const [gridOpen, setGridOpen] = useState(true);
  const [err, setErr] = useState("");
  const load = async () => {
    try { const [t, w] = await Promise.all([api.get(`/tables?branch=${branch}`), api.get(`/waiters?branch=${branch}`)]); setTables(t.data); setWaiters(w.data); return t.data; }
    catch (e) { setErr(errorMessage(e)); return []; }
  };
  const pick = async (t) => {
    setErr("");
    try {
      const { data } = t.status === "occupied" && t.current_tab_id ? await api.get(`/tabs/${t.current_tab_id}`) : await api.post(`/tables/${t.id}/open`);
      setTab(data); setGridOpen(false); load();
    } catch (e) { setErr(errorMessage(e)); }
  };
  useEffect(() => {
    load().then((list) => {
      if (initialTable?.id) { const t = list.find((x) => x.id === initialTable.id); if (t) pick(t); onInitialUsed?.(); }
    });
  }, [branch]); // eslint-disable-line
  const reloadTab = async () => {
    try { const { data } = await api.get(`/tabs/${tab.id}`); setTab(data); } catch { setTab(null); }
    load();
  };

  if (takeaway) return <TakeawayCart outlet={outlet} recipes={recipes} onCheckout={onCheckout} onBack={() => setTakeaway(false)} />;
  return (
    <div data-testid="pos-view">
      <div className="panel pos-table-selector" data-testid="pos-table-selector">
        <button className="pos-sel-head" onClick={() => setGridOpen(!gridOpen)} data-testid="pos-table-selector-toggle">
          <div><span className="overline">STEP 1 · PICK A TABLE</span><h2>{tab ? `Ordering for ${tab.table_label}` : "Select table to punch order"}</h2></div>
          {gridOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        {gridOpen && (
          <div className="pos-sel-body">
            <button className="takeaway-tile" onClick={() => setTakeaway(true)} data-testid="pos-takeaway-tile"><ShoppingBag size={20} /><b>Takeaway / Delivery</b><small>Quick order without a table → straight to Final Bill</small></button>
            <TableMap tables={tables} onOpen={pick} canManage={false} title="Dine-in tables" subtitle="Free tables open a new tab; occupied tables continue their order." />
          </div>
        )}
      </div>
      {err && <div className="auth-alert error" data-testid="pos-error">{err}</div>}
      {tab && <TabPanel mode="order" tab={tab} recipes={recipes} tables={tables} waiters={waiters} branch={branch}
        onBack={() => { setTab(null); setGridOpen(true); load(); }} onChanged={reloadTab} onGoBill={(t) => onCheckout({ tab_id: t.id })} />}
    </div>
  );
}
